import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface PageHeroProps {
    /** Upright opening letters of the wordmark. */
    lead: string
    /** Italic remainder, matching the original's split wordmark. */
    tail: string
    badges?: [string, string]
    intro?: string
    className?: string
}

/**
 * Shared page opener: one oversized word split into an upright head and an
 * italic tail, with two pill badges hanging off the wordmark's edges.
 */
export const PageHero: FC<PageHeroProps> = ({
    lead,
    tail,
    badges,
    intro,
    className
}): ReactNode => {
    const pill = 'inline-flex whitespace-nowrap rounded-full border border-hairline bg-white px-4 py-2 text-[16px] font-medium text-ink shadow-[0_10px_40px_-8px_rgba(0,0,0,0.18)] md:px-5 md:py-2.5 md:text-[18px]'

    return (
        <header
            className={cn(
                'flex flex-col items-center px-5 pb-16 pt-[125px] md:px-8 md:pb-24 md:pt-[165px] lg:pt-[175px]',
                className
            )}
        >
            <div
                className='relative w-min'
                data-reveal='up'
                data-reveal-load
                data-reveal-delay='0.4'
            >
                <h1 className='whitespace-pre text-center font-heading text-[110px] font-medium leading-[0.9] tracking-[-0.04em] text-brand md:text-[200px] lg:text-[250px]'>
                    {lead}

                    <em>
                        {tail}
                    </em>
                </h1>

                {badges && (
                    <>
                        <span
                            className={cn(pill, 'absolute right-[-25px] top-[60px] md:right-[-28px] md:top-[148px]')}
                            data-reveal='left'
                            data-reveal-load
                            data-reveal-delay='0.8'
                        >
                            {badges[0]}
                        </span>

                        <span
                            className={cn(pill, 'absolute bottom-[91px] left-[-11px] md:bottom-[128px] md:left-[-47px]')}
                            data-reveal='right'
                            data-reveal-load
                            data-reveal-delay='1'
                        >
                            {badges[1]}
                        </span>
                    </>
                )}
            </div>

            {intro && (
                <p
                    className='t-body mt-10 max-w-[620px] text-center'
                    data-reveal='up'
                >
                    {intro}
                </p>
            )}
        </header>
    )
}
